import { useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, IconButton, InputAdornment,
  Stack, TextField, Typography,
} from '@mui/material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError } from '@cmms/api-client'

import { AuthShell } from '../../layouts/AuthShell'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'

export function AcceptInvitePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const orgSlug = params.get('org') ?? ''

  const setTokens = useAuthStore((s) => s.setTokens)
  const setUser = useAuthStore((s) => s.setUser)

  const [loading, setLoading] = useState(true)
  const [valid, setValid] = useState(false)
  const [inviteData, setInviteData] = useState<{ email: string; full_name: string; role: string | null } | null>(null)

  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!token || !orgSlug) { setLoading(false); return }
    api.invitations.validate(token, orgSlug)
      .then((res) => {
        setValid(res.valid)
        if (res.valid) {
          setInviteData({ email: res.email!, full_name: res.full_name!, role: res.role })
          setName(res.full_name ?? '')
        }
      })
      .catch(() => setValid(false))
      .finally(() => setLoading(false))
  }, [token, orgSlug])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)

    if (password.length < 8) { setError('Password must be at least 8 characters'); return }
    if (password !== confirmPassword) { setError('Passwords do not match'); return }

    setSubmitting(true)
    try {
      const result = await api.invitations.accept({ org_slug: orgSlug, token, password })
      setTokens(result.access_token, result.refresh_token)
      const user = await api.auth.me()
      setUser(user)
      setSuccess(true)
      setTimeout(() => navigate('/overview', { replace: true }), 1500)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to accept invitation')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <AuthShell title="">
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={32} />
          <Typography sx={{ mt: 2, fontSize: 14, color: 'text.secondary' }}>Verifying invitation...</Typography>
        </Box>
      </AuthShell>
    )
  }

  if (!token || !orgSlug || !valid) {
    return (
      <AuthShell title="">
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>Invalid invitation</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 3 }}>
            This invitation link is invalid or has expired. Please ask your administrator to send a new one.
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/login')} sx={{ fontSize: 13, fontWeight: 600 }}>
            Go to login
          </Button>
        </Box>
      </AuthShell>
    )
  }

  if (success) {
    return (
      <AuthShell title="">
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 48, color: '#059669', mb: 2 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1 }}>Welcome aboard!</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>
            Your account has been created. Redirecting you to the dashboard...
          </Typography>
        </Box>
      </AuthShell>
    )
  }

  const orgDisplay = orgSlug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  return (
    <AuthShell title="">
      <Box sx={{ maxWidth: 420, mx: 'auto', width: '100%' }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: 2.5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: (t) => `${t.palette.primary.main}18`, color: 'primary.main', mb: 2,
          }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800 }}>{orgSlug.charAt(0).toUpperCase()}</Typography>
          </Box>
          <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
            Join {orgDisplay}
          </Typography>
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', mt: 0.75 }}>
            Set up your password to complete your account
          </Typography>
        </Box>

        <form onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            {/* Pre-filled info */}
            <TextField label="Full name" value={name} onChange={(e) => setName(e.target.value)}
              size="small" fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><PersonOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
              helperText="You can change your display name"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            <TextField label="Email" value={inviteData?.email ?? ''} size="small" fullWidth disabled
              InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
              helperText="This is the email your invitation was sent to"
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {inviteData?.role && (
              <TextField label="Role" value={inviteData.role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())} size="small" fullWidth disabled
                InputProps={{ startAdornment: <InputAdornment position="start"><BadgeOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
                helperText="Assigned by your administrator"
                sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />
            )}

            {/* Password fields */}
            <TextField label="Create password" type={showPassword ? 'text' : 'password'}
              value={password} onChange={(e) => setPassword(e.target.value)}
              size="small" fullWidth placeholder="At least 8 characters"
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            <TextField label="Confirm password" type={showPassword ? 'text' : 'password'}
              value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              size="small" fullWidth placeholder="Re-enter your password"
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment>,
              }}
              error={confirmPassword.length > 0 && password !== confirmPassword}
              helperText={confirmPassword.length > 0 && password !== confirmPassword ? 'Passwords do not match' : undefined}
              sx={{ '& .MuiOutlinedInput-root': { fontSize: 13.5 } }} />

            {error && <Alert severity="error" sx={{ fontSize: 13 }}>{error}</Alert>}

            <Button type="submit" variant="contained" fullWidth disabled={submitting || !password || !confirmPassword}
              sx={{ fontSize: 14, fontWeight: 600, py: 1.25, borderRadius: 2 }}>
              {submitting ? 'Creating account...' : 'Create account & join'}
            </Button>

            <Typography sx={{ fontSize: 12, color: 'text.disabled', textAlign: 'center' }}>
              Already have an account?{' '}
              <Typography component="a" href="/login" sx={{ fontSize: 12, color: 'primary.main', cursor: 'pointer', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                Log in instead
              </Typography>
            </Typography>
          </Stack>
        </form>
      </Box>
    </AuthShell>
  )
}
